import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc, Id } from '@cvx/_generated/dataModel'
import type { AdditionalInfoField } from '@cvx/features/jobs/additional_info_fields_db'
import type { JobQuestionNode } from '@cvx/features/jobs/job_questions_db'
import type { ScreenerQuestion } from '@cvx/features/jobs/screener_questions_db'
import { Button } from '@rekrytering/ui/components/button'
import { Switch } from '@rekrytering/ui/components/switch'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CandidateActionsTab } from '@/features/candidates/components/candidate-actions-tab'
import { OverviewTab } from '@/features/candidates/components/overview-tab'
import { AggregateReportTab } from '@/features/interviews/components/aggregate-report-tab'
import { CandidatesTab } from '@/features/interviews/components/candidates-tab'
import { PdfExportDropdown } from '@/features/interviews/components/pdf-export-dropdown'
import { PreviewInterviewModal } from '@/features/interviews/components/preview-interview-modal'
import { AdditionalInfoFieldsEditor } from '@/features/jobs/components/additional-info-fields-editor'
import { CompetencyWeightsEditor } from '@/features/jobs/components/competency-weights-editor'
import { HiringTeamEditor } from '@/features/jobs/components/hiring-team-editor'
import { InterviewKitsEditor } from '@/features/jobs/components/interview-kits-editor'
import { IntroOutroEditor } from '@/features/jobs/components/intro-outro-editor'
import { JobQuestionsEditor } from '@/features/jobs/components/job-questions-editor'
import { JobTranslationsEditor } from '@/features/jobs/components/job-translations-editor'
import { ModalityEditor } from '@/features/jobs/components/modality-editor'
import { ReminderCadenceEditor } from '@/features/jobs/components/reminder-cadence-editor'
import { ScreenerQuestionsEditor } from '@/features/jobs/components/screener-questions-editor'
import { VoiceSelector } from '@/features/jobs/components/voice-selector'

const DRAFT_TABS = [
  'questions',
  'screener',
  'additionalInfo',
  'modality',
  'voice',
] as const
const RESULTS_TABS = [
  'overview',
  'candidates',
  'candidateActions',
  'report',
  'settings',
] as const

type DraftTab = (typeof DRAFT_TABS)[number]
type ResultsTab = (typeof RESULTS_TABS)[number]

type LaunchState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'insufficient_balance'
      readonly balanceCents: number
      readonly requiredCents: number
    }
  | { readonly status: 'error' }

export function JobDetailPage() {
  const { t } = useTranslation()
  const { jobId } = useParams({ strict: false }) as { jobId: Id<'jobs'> }
  const { launch } = useSearch({ strict: false }) as { launch?: boolean }
  const [activeDraftTab, setActiveDraftTab] = useState<DraftTab>('questions')
  const [activeResultsTab, setActiveResultsTab] =
    useState<ResultsTab>('overview')
  const [launchState, setLaunchState] = useState<LaunchState>({
    status: 'idle',
  })
  const [previewOpen, setPreviewOpen] = useState(false)

  const { mutate: updateMyJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.updateMyJob),
  })

  const { mutateAsync: activateMyJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.activateMyJob),
  })

  const { mutateAsync: generateJobLaunchCheckoutUrl } = useMutation({
    mutationFn: useConvexAction(
      api.features.jobs.jobs.generateJobLaunchCheckoutUrl,
    ),
  })

  const handleLaunch = async () => {
    setLaunchState({ status: 'loading' })
    const result = await activateMyJob({ jobId })
    if (result.ok) {
      setLaunchState({ status: 'idle' })
      return
    }
    if (result.reason === 'insufficient_balance') {
      setLaunchState({
        status: 'insufficient_balance',
        balanceCents: result.balanceCents,
        requiredCents: result.requiredCents,
      })
      return
    }
    setLaunchState({ status: 'error' })
  }

  const handleTopUpAndLaunch = async () => {
    setLaunchState({ status: 'loading' })
    const result = await generateJobLaunchCheckoutUrl({
      jobId,
      productIds: [],
      origin: window.location.origin,
    })
    if (result.ok) {
      window.location.href = result.url
      return
    }
    setLaunchState({ status: 'error' })
  }

  useEffect(() => {
    if (launch) {
      handleLaunch()
    }
  }, [launch])

  const { data: jobData } = useQuery(
    convexQuery(api.features.jobs.jobs.getMyJobById, { jobId }),
  )
  const job = jobData as Doc<'jobs'> | null | undefined
  const { data: questionsData } = useQuery(
    convexQuery(api.features.jobs.jobs.listMyJobQuestions, { jobId }),
  )
  const questions = (questionsData ?? []) as JobQuestionNode[]

  const { data: screenerData } = useQuery(
    convexQuery(api.features.jobs.jobs.listMyScreenerQuestions, { jobId }),
  )
  const screenerQuestions = (screenerData ?? []) as ScreenerQuestion[]

  const { data: additionalInfoData } = useQuery(
    convexQuery(api.features.jobs.jobs.listMyAdditionalInfoFields, { jobId }),
  )
  const additionalInfoFields = (additionalInfoData ??
    []) as AdditionalInfoField[]

  if (!job) return null

  const isDraft = job.status === 'draft'
  const isLocked = job.status === 'active'

  return (
    <>
      {previewOpen && (
        <PreviewInterviewModal
          jobId={jobId}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      <div className="flex h-full w-full flex-col px-6 py-8">
        <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-2xl font-medium text-primary">{job.title}</h2>
              <p className="text-sm text-primary/60">{job.description}</p>
            </div>

            <div className="flex items-center gap-2">
              <PdfExportDropdown jobId={jobId} />
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreviewOpen(true)}
              >
                {t('jobs.preview.button')}
              </Button>

              {job.status === 'draft' && (
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleLaunch}
                      disabled={launchState.status === 'loading'}
                    >
                      {t('jobs.launch.button')}
                    </Button>
                    {launchState.status === 'insufficient_balance' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleTopUpAndLaunch}
                      >
                        {t('jobs.launch.topUpAndLaunch')}
                      </Button>
                    )}
                  </div>
                  {launchState.status === 'insufficient_balance' && (
                    <p className="text-xs text-destructive">
                      {t('jobs.launch.insufficientBalance', {
                        required: String(
                          Math.round(launchState.requiredCents / 100),
                        ),
                        balance: String(
                          Math.round(launchState.balanceCents / 100),
                        ),
                      })}
                    </p>
                  )}
                </div>
              )}

              {job.status === 'active' && (
                <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                  {t('jobs.launch.active')}
                </span>
              )}
              {job.status === 'paused' && (
                <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                  {t('jobs.statusPaused')}
                </span>
              )}
            </div>
          </div>

          {isDraft ? (
            <>
              <div className="flex gap-1 border-b border-border">
                {DRAFT_TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveDraftTab(tab)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium transition',
                      tab === activeDraftTab
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-primary/60 hover:text-primary',
                    )}
                  >
                    {t(`jobs.tabs.${tab}`)}
                  </button>
                ))}
              </div>

              {activeDraftTab === 'questions' && (
                <JobQuestionsEditor
                  jobId={jobId}
                  jobLanguage={job.language}
                  initialQuestions={questions}
                  isLocked={false}
                />
              )}
              {activeDraftTab === 'screener' && (
                <ScreenerQuestionsEditor
                  jobId={jobId}
                  jobLanguage={job.language}
                  initialQuestions={screenerQuestions}
                  isLocked={false}
                />
              )}
              {activeDraftTab === 'additionalInfo' && (
                <AdditionalInfoFieldsEditor
                  jobId={jobId}
                  initialFields={additionalInfoFields}
                  isLocked={false}
                />
              )}
              {activeDraftTab === 'modality' && (
                <ModalityEditor
                  jobId={jobId}
                  allowedModalities={job.allowedModalities}
                  isLocked={false}
                />
              )}
              {activeDraftTab === 'voice' && (
                <VoiceSelector
                  jobId={jobId}
                  selectedVoiceId={job.voiceId}
                  isLocked={false}
                />
              )}
            </>
          ) : (
            <>
              <div className="flex gap-1 border-b border-border">
                {RESULTS_TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveResultsTab(tab)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium transition',
                      tab === activeResultsTab
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-primary/60 hover:text-primary',
                    )}
                  >
                    {t(`jobs.tabs.${tab}`)}
                  </button>
                ))}
              </div>

              {activeResultsTab === 'overview' && <OverviewTab jobId={jobId} />}
              {activeResultsTab === 'candidates' && (
                <CandidatesTab
                  jobId={jobId}
                  interviewCount={job.interviewCount}
                  isActive={job.status === 'active'}
                />
              )}
              {activeResultsTab === 'candidateActions' && (
                <CandidateActionsTab
                  jobId={jobId}
                  hasCalendlyUrl={!!job.calendlyUrl}
                  calendlyUrl={job.calendlyUrl}
                />
              )}
              {activeResultsTab === 'report' && (
                <AggregateReportTab jobId={jobId} />
              )}
              {activeResultsTab === 'settings' && (
                <div className="flex flex-col gap-8">
                  <section className="flex flex-col gap-4">
                    <h3 className="text-sm font-medium text-primary/70">
                      {t('jobs.tabs.questions')}
                    </h3>
                    <JobQuestionsEditor
                      jobId={jobId}
                      jobLanguage={job.language}
                      initialQuestions={questions}
                      isLocked={isLocked}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-sm font-medium text-primary/70">
                      {t('jobs.tabs.screener')}
                    </h3>
                    <ScreenerQuestionsEditor
                      jobId={jobId}
                      jobLanguage={job.language}
                      initialQuestions={screenerQuestions}
                      isLocked={isLocked}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-sm font-medium text-primary/70">
                      {t('jobs.tabs.additionalInfo')}
                    </h3>
                    <AdditionalInfoFieldsEditor
                      jobId={jobId}
                      initialFields={additionalInfoFields}
                      isLocked={isLocked}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-sm font-medium text-primary/70">
                      {t('jobs.tabs.modality')}
                    </h3>
                    <ModalityEditor
                      jobId={jobId}
                      allowedModalities={job.allowedModalities}
                      isLocked={isLocked}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-sm font-medium text-primary/70">
                      {t('jobs.tabs.voice')}
                    </h3>
                    <VoiceSelector
                      jobId={jobId}
                      selectedVoiceId={job.voiceId}
                      isLocked={isLocked}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-sm font-medium text-primary/70">
                      {t('jobs.settings.reminderCadenceTitle')}
                    </h3>
                    <ReminderCadenceEditor
                      jobId={jobId}
                      reminderDays={job.reminderDays}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-sm font-medium text-primary/70">
                      {t('jobs.settings.competencyWeightsTitle')}
                    </h3>
                    <CompetencyWeightsEditor
                      jobId={jobId}
                      questions={questions}
                      competencyWeights={job.competencyWeights}
                      isLocked={isLocked}
                    />
                  </section>
                  {job.language === 'both' && (
                    <section className="flex flex-col gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-primary/70">
                          {t('jobs.settings.translationsTitle')}
                        </h3>
                        <p className="text-xs text-primary/50 mt-0.5">
                          {t('jobs.settings.translationsDescription')}
                        </p>
                      </div>
                      <JobTranslationsEditor job={job} />
                    </section>
                  )}
                  <section className="flex flex-col gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-primary/70">
                        {t('jobs.settings.introMessageTitle')}
                      </h3>
                      <p className="text-xs text-primary/50 mt-0.5">
                        {t('jobs.settings.introMessageDescription')}
                      </p>
                    </div>
                    <IntroOutroEditor
                      jobId={jobId}
                      field="introMessage"
                      initialValue={job.introMessage}
                      jobLanguage={job.language}
                      initialTranslation={job.introMessageTranslations?.['en']}
                      jobTitle={job.title}
                      jobDescription={job.description ?? ''}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-primary/70">
                        {t('jobs.settings.outroMessageTitle')}
                      </h3>
                      <p className="text-xs text-primary/50 mt-0.5">
                        {t('jobs.settings.outroMessageDescription')}
                      </p>
                    </div>
                    <IntroOutroEditor
                      jobId={jobId}
                      field="outroMessage"
                      initialValue={job.outroMessage}
                      jobLanguage={job.language}
                      initialTranslation={job.outroMessageTranslations?.['en']}
                      jobTitle={job.title}
                      jobDescription={job.description ?? ''}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-primary/70">
                        {t('jobs.settings.candidateFeedbackTitle')}
                      </h3>
                      <p className="text-xs text-primary/50 mt-0.5">
                        {t('jobs.settings.candidateFeedbackDescription')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={job.candidateFeedbackEnabled ?? false}
                        onCheckedChange={(checked) =>
                          updateMyJob({
                            jobId,
                            candidateFeedbackEnabled: checked,
                          })
                        }
                      />
                      <span className="text-sm text-primary/70">
                        {t('jobs.settings.candidateFeedbackEnabled')}
                      </span>
                    </div>
                  </section>
                  <section className="flex flex-col gap-4">
                    <HiringTeamEditor jobId={jobId} />
                  </section>
                  <section className="flex flex-col gap-4">
                    <InterviewKitsEditor jobId={jobId} />
                  </section>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
